import type { Order } from '../../types/order';
import { hasRevenueForMarginPercent } from './orderFinanceDisplay';
import { getOrderMarginTone, OrderMarginTone } from './orderMarginPresentation';

/**
 * Order-level margin facts from the backend cost model
 * (`orderCostBreakdownService`: COGS + manual costs + supplier-order allocation).
 *
 * `totalCost` / `margin` / `marginPercent` are `null` when the cost is unknown:
 * `responseFilter.filterOrderData` nulls them for `WAREHOUSE_FIFO` orders whose
 * warehouse cost is still pending. Unknown is not zero.
 */
export type OrderMarginFacts = Pick<
  Order,
  'costBasis' | 'revenue' | 'totalCost' | 'margin' | 'marginPercent'
>;

/**
 * Why an order margin percent cannot be shown. `NO_REVENUE` and `NO_COST` are
 * both "undefined percent", never a fabricated `0%` or `100%` — the same rule
 * the profitability report applies through `displayMarginPercent`/`getMarginBucket`.
 */
export type OrderMarginPercentState = 'KNOWN' | 'NO_REVENUE' | 'NO_COST';

export const ORDER_MARGIN_PERCENT_UNAVAILABLE_KEYS = {
  NO_REVENUE: 'orders:orderDetail.marginPercentUnavailable',
  NO_COST: 'orders:orderDetail.marginPercentNoCost',
} as const;

/** Money is stored in PLN (migration 054); a missing amount displays as zero. */
export const toOrderMarginAmount = (value: number | null | undefined): number => Number(value ?? 0);

export function getOrderMarginPercentState(
  facts: Pick<OrderMarginFacts, 'revenue' | 'totalCost'>,
): OrderMarginPercentState {
  if (!hasRevenueForMarginPercent(toOrderMarginAmount(facts.revenue))) return 'NO_REVENUE';
  if (toOrderMarginAmount(facts.totalCost) <= 0) return 'NO_COST';
  return 'KNOWN';
}

export interface OrderMarginSummary {
  revenue: number;
  totalCost: number;
  margin: number;
  tone: OrderMarginTone;
  /** `orders:orderDetail.<statusKey>` describing the result. */
  statusKey: 'marginResultPositive' | 'marginResultNegative' | 'marginResultWatch';
  percentState: OrderMarginPercentState;
  /** Percent to format, or `null` when `percentUnavailableKey` must be shown instead. */
  percent: number | null;
  percentUnavailableKey: string | null;
}

/**
 * Single home for how an order's cost-model margin is presented. Used by the
 * financial summary on the finances tab and by the cost-model margin section on
 * the order-information tab, so both surfaces show the same number the same way.
 */
export function getOrderMarginSummary(facts: OrderMarginFacts): OrderMarginSummary {
  const revenue = toOrderMarginAmount(facts.revenue);
  const totalCost = toOrderMarginAmount(facts.totalCost);
  const margin = toOrderMarginAmount(facts.margin);
  const marginPercent = toOrderMarginAmount(facts.marginPercent);
  const tone = getOrderMarginTone(margin, marginPercent);
  const percentState = getOrderMarginPercentState(facts);

  return {
    revenue,
    totalCost,
    margin,
    tone,
    statusKey: tone === 'loss'
      ? 'marginResultNegative'
      : tone === 'watch'
        ? 'marginResultWatch'
        : 'marginResultPositive',
    percentState,
    percent: percentState === 'KNOWN' ? marginPercent : null,
    percentUnavailableKey: percentState === 'KNOWN'
      ? null
      : ORDER_MARGIN_PERCENT_UNAVAILABLE_KEYS[percentState],
  };
}

/**
 * An order has a cost-model margin worth showing only once the backend
 * published a cost or a margin. A pending warehouse cost stays hidden rather
 * than rendering a zero result.
 */
export function hasOrderCostModelMargin(facts: OrderMarginFacts | undefined | null): boolean {
  return !!facts && (facts.margin != null || facts.totalCost != null);
}
